import { useState } from 'react';
import { Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert, Tabs, Tab } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { importHistoricalSales } from '../../api/salesApi';
import { importCities } from '../../api/cityApi';
import { bulkUpdateCustomersBySalesId } from '../../api/customerApi';

const SALES_HEADERS = ['CustomerName', 'CustomerPhone', 'CustomerEmail', 'CustomerAddress', 'AgentUsername', 'CoAgentUsername', 'ProductCategory', 'PanelQty', 'Energy', 'OriginalPrice', 'DiscountByAstern', 'DiscountByAgent', 'FinalPrice', 'AddOnPrice', 'PaymentMethod', 'DepositDate', 'Status', 'Scheme'];
const CITY_HEADERS = ['State', 'City'];
const CUSTOMER_UPDATE_HEADERS = ['SalesId', 'CustomerName', 'PhoneNumber', 'Email', 'Address', 'State', 'City', 'Postcode'];

// Quote-aware CSV parser. Splits on commas EXCEPT inside double-quoted
// fields, and treats `""` inside a quoted field as a literal quote. Also
// handles \r\n line endings and rows that span newlines (within quotes).
function parseCSV(text) {
    // Strip BOM if present.
    if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

    const rows = [];
    let field = '';
    let row = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }   // escaped ""
                else { inQuotes = false; }                        // closing quote
            } else {
                field += c;
            }
        } else {
            if (c === '"') {
                inQuotes = true;
            } else if (c === ',') {
                row.push(field); field = '';
            } else if (c === '\n') {
                row.push(field); field = '';
                rows.push(row); row = [];
            } else if (c === '\r') {
                // skip (handled by \n)
            } else {
                field += c;
            }
        }
    }
    // Trailing field / row.
    if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }

    // Drop fully-empty rows.
    const nonEmpty = rows.filter(r => r.some(v => v && v.trim()));
    if (nonEmpty.length < 2) return [];

    const headers = nonEmpty[0].map(h => h.trim());
    return nonEmpty.slice(1).map(values => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (values[i] ?? '').trim(); });
        return obj;
    });
}

function downloadCSV(filename, headers, sampleRows) {
    const csv = headers.join(',') + '\n' + sampleRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

const cellSx = { fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", padding: '4px 8px', color: '#374151' };
const headSx = { ...cellSx, fontWeight: 600, color: '#64748B', backgroundColor: '#F8FAFC', textTransform: 'uppercase', fontSize: '0.65rem' };

function SalesImportTab() {
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState('');
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDownloadTemplate = () => {
        downloadCSV('import_sales_template.csv', SALES_HEADERS, [
            'Ahmad Razak,0123456789,ahmad@email.com,"No 12, Jalan SS2",phangkai,,RESIDENTIAL,11,6.82,18800,1720,1720,15360,0,CASH,2025-06-15,,NEM',
            'Syarikat ABC Sdn Bhd,0398765432,abc@company.com,"Lot 5, Jalan Industri",phangkai,junhui,COMMERCIAL,50,30.00,95000,5000,3000,87000,2500,CASH,2025-07-20,,NEM',
        ]);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        setResult(null);
        const reader = new FileReader();
        reader.onload = (ev) => setRows(parseCSV(ev.target.result));
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!rows.length) return;
        if (!window.confirm(`Import ${rows.length} historical sales (NEM scheme, no commission)?`)) return;
        setImporting(true);
        try {
            const mapped = rows.map(r => ({
                customerName: r.CustomerName || r.customerName || '',
                customerPhone: r.CustomerPhone || r.customerPhone || '',
                customerEmail: r.CustomerEmail || r.customerEmail || '',
                customerAddress: r.CustomerAddress || r.customerAddress || '',
                agentUsername: r.AgentUsername || r.agentUsername || '',
                coAgentUsername: r.CoAgentUsername || r.coAgentUsername || '',
                productCategory: r.ProductCategory || r.productCategory || '',
                panelQty: parseInt(r.PanelQty || r.panelQty) || 0,
                energy: parseFloat(r.Energy || r.energy) || 0,
                originalPrice: parseFloat(r.OriginalPrice || r.originalPrice) || 0,
                discountByAstern: parseFloat(r.DiscountByAstern || r.discountByAstern) || 0,
                discountByAgent: parseFloat(r.DiscountByAgent || r.discountByAgent) || 0,
                finalPrice: parseFloat(r.FinalPrice || r.finalPrice) || 0,
                addOnPrice: parseFloat(r.AddOnPrice || r.addOnPrice) || 0,
                paymentMethod: r.PaymentMethod || r.paymentMethod || '',
                depositDate: r.DepositDate || r.depositDate || '',
                status: r.Status || r.status || '',
                scheme: r.Scheme || r.scheme || 'NEM',
            }));
            const res = await importHistoricalSales(mapped);
            setResult(res.data);
        } catch (err) {
            const msg = typeof err.response?.data === 'string' ? err.response.data : JSON.stringify(err.response?.data) || err.message;
            alert('Import failed: ' + msg);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                    Import previous sales (NEM scheme) — no commission will be calculated
                </p>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}
                    sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px' }}>
                    Download CSV Template
                </Button>
                <Button component="label" variant="contained" startIcon={<CloudUploadIcon />}
                    sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px', boxShadow: 'none' }}>
                    {fileName || 'Upload CSV'}
                    <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
                </Button>
            </Box>

            {result && (
                <Alert severity={result.skipped > 0 ? 'warning' : 'success'} sx={{ mb: 2, fontFamily: "'Poppins', sans-serif", borderRadius: '8px' }}>
                    Imported: {result.imported} / {result.totalRows} | Skipped: {result.skipped}
                    {result.errors?.length > 0 && (
                        <Box sx={{ mt: 1, fontSize: '0.72rem' }}>
                            {result.errors.map((e, i) => <div key={i}>- {e}</div>)}
                        </Box>
                    )}
                </Alert>
            )}

            {rows.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                            Preview: {rows.length} rows
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label="NEM" size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: '0.7rem' }} />
                            <Chip label="No Commission" size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: '0.7rem' }} />
                            <Button variant="contained" color="success" onClick={handleImport} disabled={importing}
                                sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px', fontWeight: 600 }}>
                                {importing ? 'Importing...' : `Import ${rows.length} Sales`}
                            </Button>
                        </Box>
                    </Box>
                    <TableContainer sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headSx}>#</TableCell>
                                    {SALES_HEADERS.map(h => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                        <TableCell sx={cellSx}>{i + 1}</TableCell>
                                        {SALES_HEADERS.map(h => (
                                            <TableCell key={h} sx={{ ...cellSx, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row[h] || row[h.charAt(0).toLowerCase() + h.slice(1)] || '—'}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </div>
    );
}

function CityImportTab() {
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState('');
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDownloadTemplate = () => {
        downloadCSV('import_cities_template.csv', CITY_HEADERS, [
            'Pulau Pinang,Bayan Lepas',
            'Selangor,Petaling Jaya',
            'Johor,Johor Bahru',
        ]);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        setResult(null);
        const reader = new FileReader();
        reader.onload = (ev) => setRows(parseCSV(ev.target.result));
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!rows.length) return;
        if (!window.confirm(`Import ${rows.length} cities? Existing entries will update; new ones will be added; missing ones stay as-is.`)) return;
        setImporting(true);
        try {
            const mapped = rows.map(r => ({
                state: r.State || r.state || '',
                city: r.City || r.city || '',
            }));
            const res = await importCities(mapped);
            setResult(res.data);
        } catch (err) {
            const msg = typeof err.response?.data === 'string' ? err.response.data : JSON.stringify(err.response?.data) || err.message;
            alert('Import failed: ' + msg);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                    Import / update the master state + city list. Used for the State and City dropdowns when creating a sale.
                </p>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}
                    sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px' }}>
                    Download CSV Template
                </Button>
                <Button component="label" variant="contained" startIcon={<CloudUploadIcon />}
                    sx={{ background: 'linear-gradient(135deg, #0F766E, #0E7490)', textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px', boxShadow: 'none' }}>
                    {fileName || 'Upload CSV'}
                    <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
                </Button>
            </Box>

            {result && (
                <Alert severity={(result.errors?.length ?? 0) > 0 ? 'warning' : 'success'} sx={{ mb: 2, fontFamily: "'Poppins', sans-serif", borderRadius: '8px' }}>
                    Added: {result.added} · Updated: {result.updated} · Unchanged: {result.unchanged}
                    {result.errors?.length > 0 && (
                        <Box sx={{ mt: 1, fontSize: '0.72rem' }}>
                            {result.errors.map((e, i) => <div key={i}>- {e}</div>)}
                        </Box>
                    )}
                </Alert>
            )}

            {rows.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                            Preview: {rows.length} rows
                        </Typography>
                        <Button variant="contained" color="success" onClick={handleImport} disabled={importing}
                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px', fontWeight: 600 }}>
                            {importing ? 'Importing...' : `Import ${rows.length} Cities`}
                        </Button>
                    </Box>
                    <TableContainer sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headSx}>#</TableCell>
                                    {CITY_HEADERS.map(h => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                        <TableCell sx={cellSx}>{i + 1}</TableCell>
                                        {CITY_HEADERS.map(h => (
                                            <TableCell key={h} sx={cellSx}>
                                                {row[h] || row[h.toLowerCase()] || '—'}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </div>
    );
}

// Update customer fields (address / phone / email / etc.) on existing
// sales by SalesId. Empty CSV cells are ignored — sparse uploads are fine.
function CustomerUpdateTab() {
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState('');
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDownloadTemplate = () => {
        downloadCSV('update_customers_template.csv', CUSTOMER_UPDATE_HEADERS, [
            '1234,Ahmad Razak,0123456789,ahmad@email.com,"No 12, Jalan SS2",Selangor,Petaling Jaya,47301',
            '1235,,,,,Selangor,Subang Jaya,47500',
        ]);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileName(file.name);
        setResult(null);
        const reader = new FileReader();
        reader.onload = (ev) => setRows(parseCSV(ev.target.result));
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!rows.length) return;
        if (!window.confirm(`Update customer details on ${rows.length} sale(s)? Empty cells stay unchanged.`)) return;
        setImporting(true);
        try {
            const mapped = rows.map(r => ({
                salesId: parseInt(r.SalesId || r.salesId) || 0,
                customerName: (r.CustomerName || r.customerName || '').trim() || null,
                phoneNumber: (r.PhoneNumber || r.phoneNumber || '').trim() || null,
                email: (r.Email || r.email || '').trim() || null,
                address: (r.Address || r.address || '').trim() || null,
                state: (r.State || r.state || '').trim() || null,
                city: (r.City || r.city || '').trim() || null,
                postcode: (r.Postcode || r.postcode || '').trim() || null,
            }));
            const res = await bulkUpdateCustomersBySalesId(mapped);
            setResult(res.data);
        } catch (err) {
            const msg = typeof err.response?.data === 'string' ? err.response.data : JSON.stringify(err.response?.data) || err.message;
            alert('Update failed: ' + msg);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                    Update customer info (name / phone / email / address / state / city / postcode) on existing sales.
                    Match key is <strong>SalesId</strong>. Leave a column blank to keep its current value.
                </p>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}
                    sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px' }}>
                    Download CSV Template
                </Button>
                <Button component="label" variant="contained" startIcon={<CloudUploadIcon />}
                    sx={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px', boxShadow: 'none' }}>
                    {fileName || 'Upload CSV'}
                    <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
                </Button>
            </Box>

            {result && (
                <>
                    <Alert severity={(result.notFound ?? 0) > 0 ? 'warning' : 'success'} sx={{ mb: 2, fontFamily: "'Poppins', sans-serif", borderRadius: '8px' }}>
                        Updated: {result.updated} · Not Found: {result.notFound} · No Change: {result.noChange} / Total: {result.totalRows}
                    </Alert>
                    {result.outcomes?.length > 0 && (
                        <TableContainer sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxHeight: 360, overflow: 'auto', mb: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headSx}>#</TableCell>
                                        <TableCell sx={headSx}>SalesId</TableCell>
                                        <TableCell sx={headSx}>Status</TableCell>
                                        <TableCell sx={headSx}>Fields Changed</TableCell>
                                        <TableCell sx={headSx}>Note</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {result.outcomes.map((o, i) => {
                                        const cfg = o.status === 'Updated'
                                            ? { bg: '#DCFCE7', color: '#166534' }
                                            : o.status === 'NotFound'
                                                ? { bg: '#FEE2E2', color: '#991B1B' }
                                                : { bg: '#F1F5F9', color: '#64748B' };
                                        return (
                                            <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                                <TableCell sx={cellSx}>{i + 1}</TableCell>
                                                <TableCell sx={cellSx}>{o.salesId}</TableCell>
                                                <TableCell sx={cellSx}>
                                                    <Chip label={o.status} size="small" sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem' }} />
                                                </TableCell>
                                                <TableCell sx={cellSx}>{o.fieldsChanged?.join(', ') || '—'}</TableCell>
                                                <TableCell sx={cellSx}>{o.note || '—'}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {rows.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                            Preview: {rows.length} rows
                        </Typography>
                        <Button variant="contained" color="success" onClick={handleImport} disabled={importing}
                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', borderRadius: '8px', fontWeight: 600 }}>
                            {importing ? 'Updating...' : `Update ${rows.length} Sale(s)`}
                        </Button>
                    </Box>
                    <TableContainer sx={{ backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headSx}>#</TableCell>
                                    {CUSTOMER_UPDATE_HEADERS.map(h => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                        <TableCell sx={cellSx}>{i + 1}</TableCell>
                                        {CUSTOMER_UPDATE_HEADERS.map(h => (
                                            <TableCell key={h} sx={{ ...cellSx, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row[h] || row[h.charAt(0).toLowerCase() + h.slice(1)] || '—'}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </div>
    );
}

// Single-purpose page: upload a CSV keyed on SalesId, update customer
// fields on the matching sales. Empty cells are skipped. Historical sales
// + city seed imports were retired — they were one-off setup tools and
// admin can re-add them later if needed.
export default function ImportSales() {
    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>Bulk Update Sales</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                    Upload a CSV keyed on SalesId. Non-empty cells overwrite the linked customer's fields; empty cells are skipped.
                </p>
            </Box>
            <CustomerUpdateTab />
        </div>
    );
}
